// src/services/admin.report.service.js
import { apiClient } from "@/config/api.config";

/**
 * Service to handle all report-related API calls for admin
 */
class AdminReportService {
  /**
   * Generate a report for a specific user
   * @param {string} userId - User ID
   * @param {Date} startDate - Start date for report
   * @param {Date} endDate - End date for report
   * @param {string} format - Report format (PDF, CSV, EXCEL)
   * @returns {Promise} - Promise with report generation data
   */
  async generateUserReport(userId, startDate, endDate, format = "PDF") {
    try {
      const response = await apiClient.post(
        `/reports/admin/user-report/${userId}`,
        {
          startDate,
          endDate,
          format,
        }
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Generate a report for all users
   * @param {Date} startDate - Start date for report
   * @param {Date} endDate - End date for report
   * @param {string} format - Report format (PDF, CSV, EXCEL)
   * @param {Object} filters - Optional filters for users
   * @returns {Promise} - Promise with report generation data
   */
  async generateAllUsersReport(
    startDate,
    endDate,
    format = "PDF",
    filters = {}
  ) {
    try {
      const response = await apiClient.post(`/reports/admin/all-users-report`, {
        startDate,
        endDate,
        format,
        filters,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Get report status
   * @param {string} reportId - Report ID
   * @returns {Promise} - Promise with report status
   */
  async getReportStatus(reportId) {
    try {
      const response = await apiClient.get(
        `/reports/admin/user-report/${reportId}`
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Get all reports with pagination and filtering
   * @param {Object} params - Query parameters
   * @returns {Promise} - Promise with reports data
   */
  async getAllReports(params = {}) {
    try {
      const response = await apiClient.get(`/reports/admin/user-reports`, {
        params,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Get download URL for a report
   * @param {string} reportId - Report ID
   * @returns {string} - Download URL
   */
  getReportDownloadUrl(reportId) {
    return `${apiClient.defaults.baseURL}/reports/admin/user-report/${reportId}/download`;
  }

  /**
   * Download a report directly (triggers browser download)
   * @param {string} reportId - Report ID
   */
  downloadReport(reportId) {
    // Create a hidden anchor element to trigger the download
    const downloadLink = document.createElement("a");
    downloadLink.href = this.getReportDownloadUrl(reportId);
    downloadLink.setAttribute("download", ""); // This will use the server's suggested filename
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  /**
   * Generate a financial report for a specific user
   * @param {string} userId - User ID
   * @param {Object} params - Report parameters
   * @param {Date} params.startDate - Start date for report
   * @param {Date} params.endDate - End date for report
   * @param {string} params.format - Report format (PDF, CSV, EXCEL)
   * @param {boolean} params.includePortfolio - Whether to include portfolio details
   * @returns {Promise} - Promise with report generation data
   */
  async generateUserFinancialReport(userId, params) {
    try {
      const response = await apiClient.post(
        `/reports/user/${userId}/financial-report`,
        params
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Generate financial reports for all users
   * @param {Object} params - Report parameters
   * @param {Date} params.startDate - Start date for report
   * @param {Date} params.endDate - End date for report
   * @param {string} params.format - Report format (PDF, CSV, EXCEL)
   * @param {boolean} params.includePortfolio - Whether to include portfolio details
   * @returns {Promise} - Promise with report generation data
   */
  async generateAllUsersFinancialReport(params) {
    try {
      const response = await apiClient.post(
        `/reports/users/all/financial-report`,
        params
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Download consolidated financial report for all users
   * @param {string} reportId - Report ID
   * @returns {string} - Download URL
   */
  getFinancialReportDownloadUrl(reportId) {
    return `${apiClient.defaults.baseURL}/reports/FinancialReports/${reportId}/download`;
  }

  /**
   * Download a financial report directly (triggers browser download)
   * @param {string} reportId - Report ID
   */
  downloadFinancialReport(reportId) {
    const downloadLink = document.createElement("a");
    downloadLink.href = this.getFinancialReportDownloadUrl(reportId);
    downloadLink.setAttribute("download", ""); // This will use the server's suggested filename
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  // =================== CONTACT EXPORT METHODS ===================

  /**
   * Export contacts to Excel
   * @param {Object} params - Export parameters
   * @param {string} params.status - Status filter (new,in-progress,completed)
   * @param {Date} params.startDate - Start date filter
   * @param {Date} params.endDate - End date filter
   * @param {number} params.limit - Limit number of records
   * @param {string} params.format - Export format (xlsx, csv)
   * @returns {Promise} - Promise with export data
   */
  async exportContactsToExcel(params = {}) {
    try {
      const response = await apiClient.get("/reports/contacts/export/excel", {
        params,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Get available export fields for contacts
   * @returns {Promise} - Promise with available fields
   */
  async getContactExportFields() {
    try {
      const response = await apiClient.get("/reports/contacts/export/fields");
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Preview contact export data (first 10 records)
   * @param {Object} params - Preview parameters
   * @returns {Promise} - Promise with preview data
   */
  async previewContactExport(params = {}) {
    try {
      const response = await apiClient.get("/reports/contacts/export/preview", {
        params,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Download contacts export file directly
   * @param {Object} params - Export parameters
   * @param {string} filename - Optional filename
   */
  async downloadContactsExport(params = {}, filename = "contacts_export") {
    try {
      const response = await apiClient.get("/reports/contacts/export/excel", {
        params,
        responseType: "blob",
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Determine file extension based on format
      const extension = params.format === "csv" ? "csv" : "xlsx";
      link.setAttribute("download", `${filename}.${extension}`);
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Download a transaction report directly
   * @param {string} reportId - Report ID
   */
  async downloadTransactionReport(reportId, format = "PDF") {
    try {
      const response = await apiClient.get(
        `/reports/admin/reports/${reportId}/download`,
        {
          responseType: "blob",
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Try to determine filename from header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = `transaction_report_${reportId}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      } else {
        filename += format === "EXCEL" ? ".xlsx" : format === "CSV" ? ".csv" : ".pdf";
      }

      link.setAttribute("download", filename);
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw this._handleError(error);
    }
  }

  // =================== UTILITY METHODS ===================

  /**
   * Build query parameters for exports
   * @param {Object} filters - Filter object
   * @returns {Object} - Clean query parameters
   */
  buildExportParams(filters = {}) {
    const params = {};

    if (filters.status) {
      params.status = Array.isArray(filters.status)
        ? filters.status.join(",")
        : filters.status;
    }

    if (filters.startDate) {
      params.startDate =
        filters.startDate instanceof Date
          ? filters.startDate.toISOString().split("T")[0]
          : filters.startDate;
    }

    if (filters.endDate) {
      params.endDate =
        filters.endDate instanceof Date
          ? filters.endDate.toISOString().split("T")[0]
          : filters.endDate;
    }

    if (filters.limit) {
      params.limit = filters.limit;
    }

    if (filters.format) {
      params.format = filters.format;
    }

    return params;
  }

  /**
   * Validate export parameters
   * @param {Object} params - Parameters to validate
   * @returns {Object} - Validation result
   */
  validateExportParams(params) {
    const errors = [];

    if (params.startDate && params.endDate) {
      const start = new Date(params.startDate);
      const end = new Date(params.endDate);

      if (start > end) {
        errors.push("Start date must be before end date");
      }
    }

    if (params.limit && (params.limit < 1 || params.limit > 10000)) {
      errors.push("Limit must be between 1 and 10000");
    }

    if (params.format && !["xlsx", "csv", "PDF"].includes(params.format)) {
      errors.push("Format must be one of: xlsx, csv, PDF");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Handle API errors
   * @private
   * @param {Error} error - Error object
   * @returns {Error} - Processed error
   */
  _handleError(error) {
    const errorMessage =
      error.response?.data?.message || error.message || "Something went wrong";

    return {
      message: errorMessage,
      status: error.response?.status || 500,
      isApiError: true,
    };
  }
}

export const adminReportService = new AdminReportService();
